import { z } from 'zod';

import { t } from '../i18n/validation.js';
import { SignalType } from '../types';

export const MAX_HOPPING_FREQUENCY_RANGE_MHZ = 300;
export const MAX_SOLID_FREQUENCY_RANGE_MHZ = 100;
export const MAX_MULTI_FREQUENCIES = 10;
/**
 * Mirrors MIN_MULTI_CHANNELS in backend/src/broadband-suppressions/constants.ts. The comment is
 * documentation only — each authority is pinned by its own suite from both sides.
 */
export const MIN_MULTI_FREQUENCIES = 2;
export const MAX_MULTI_FREQUENCY_SPAN_MHZ = 500;

const finiteNumber = (message: string) =>
  z.coerce.number({ error: message }).refine(Number.isFinite, message);

const requiredFiniteNumber = (message: string) =>
  z.preprocess(
    (value) => {
      if (typeof value !== 'string') return value;

      const trimmed = value.trim();
      return trimmed === '' ? undefined : Number(trimmed);
    },
    z.number({ error: message }).refine(Number.isFinite, message),
  );

const buildFrequencySchema = (
  invalidMessage: string,
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
) => {
  let schema: z.ZodType<number, unknown> = requiredFiniteNumber(invalidMessage).refine(
    (value) => value > 0,
    {
      message: invalidMessage,
    },
  );

  if (minAllowedMhz !== null && minAllowedMhz > 0) {
    schema = schema.refine((value) => value >= minAllowedMhz, {
      message: t('validation.frequency.frequencyMin', { min: minAllowedMhz }),
    });
  }

  if (maxAllowedMhz !== null && maxAllowedMhz > 0) {
    schema = schema.refine((value) => value <= maxAllowedMhz, {
      message: t('validation.frequency.frequencyMax', { max: maxAllowedMhz }),
    });
  }

  return schema;
};

const buildIncrementSchema = (
  invalidKey: string,
  rangeKey: string,
  min: number,
  max: number,
  divisibleBy?: number,
  divisibleKey?: string,
) => {
  let schema: z.ZodType<number, unknown> = finiteNumber(t(invalidKey))
    .refine((value) => Number.isInteger(value), {
      message: t(invalidKey),
    })
    .refine((value) => value >= min && value <= max, {
      message: t(rangeKey, { min, max }),
    });

  if (divisibleBy != null && divisibleKey != null) {
    schema = schema.refine((value) => value % divisibleBy === 0, {
      message: t(divisibleKey),
    });
  }

  return schema;
};

const buildDurationSchema = (maxSuppressionTime?: number | null) => {
  const schema = finiteNumber(t('validation.suppression.durationInvalid'))
    .refine((value) => Number.isInteger(value), {
      message: t('validation.suppression.durationInvalid'),
    })
    .refine((value) => value >= 1, {
      message: t('validation.suppression.durationMin'),
    });

  return maxSuppressionTime != null && maxSuppressionTime > 0
    ? schema.refine((value) => value <= maxSuppressionTime, {
        message: t('validation.suppression.durationMax', { max: maxSuppressionTime }),
      })
    : schema;
};

const buildSharedRangeFields = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
  maxSuppressionTime?: number | null,
) => {
  const durationSchema = buildDurationSchema(maxSuppressionTime);

  return {
    channelFrequencyStart: buildFrequencySchema(
      t('validation.frequency.startInvalid'),
      minAllowedMhz,
      maxAllowedMhz,
    ),
    channelFrequencyStop: buildFrequencySchema(
      t('validation.frequency.endInvalid'),
      minAllowedMhz,
      maxAllowedMhz,
    ),
    duration: durationSchema,
  };
};

const refineFrequencyRange = <
  Schema extends z.ZodType<{ channelFrequencyStart: number; channelFrequencyStop: number }>,
>(
  schema: Schema,
  maxRangeMhz: number,
) =>
  schema
    .refine((values) => values.channelFrequencyStop >= values.channelFrequencyStart, {
      path: ['channelFrequencyStop'],
      message: t('validation.frequency.endGreaterThanStart'),
    })
    .refine((values) => values.channelFrequencyStop - values.channelFrequencyStart <= maxRangeMhz, {
      path: ['channelFrequencyStop'],
      message: t('validation.broadbandSuppression.frequencyRangeMax', { max: maxRangeMhz }),
    });

export const buildBroadbandSuppressionSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
  maxSuppressionTime?: number | null,
) =>
  refineFrequencyRange(
    z.object({
      ...buildSharedRangeFields(minAllowedMhz, maxAllowedMhz, maxSuppressionTime),
      bandwidth: buildIncrementSchema(
        'validation.broadbandSuppression.bandwidthInvalid',
        'validation.broadbandSuppression.bandwidthRange',
        2,
        50,
      ),
      step: buildIncrementSchema(
        'validation.broadbandSuppression.stepInvalid',
        'validation.broadbandSuppression.stepRange',
        2,
        50,
      ),
      stepStandingTimeMs: buildIncrementSchema(
        'validation.broadbandSuppression.stepStandingTimeInvalid',
        'validation.broadbandSuppression.stepStandingTimeRange',
        5,
        100,
        5,
        'validation.broadbandSuppression.stepStandingTimeStep',
      ),
    }),
    MAX_HOPPING_FREQUENCY_RANGE_MHZ,
  );

export const buildSolidSuppressionSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
  maxSuppressionTime?: number | null,
) =>
  refineFrequencyRange(
    z.object(buildSharedRangeFields(minAllowedMhz, maxAllowedMhz, maxSuppressionTime)),
    MAX_SOLID_FREQUENCY_RANGE_MHZ,
  );

/** Rules for a single frequency of the multi-frequency list, in MHz. */
export const buildMultiFrequencyValueSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
) => buildFrequencySchema(t('validation.frequency.frequencyInvalid'), minAllowedMhz, maxAllowedMhz);

/**
 * Cross-value rules for multi-frequency entries: per-value range, uniqueness, span, and the
 * `MAX_MULTI_FREQUENCIES` cap. List-level issues carry an empty path so they surface on the array
 * itself (`errors.frequencies`) rather than on a row — the panel commits values one at a time
 * through a single input, and validates each candidate list against this schema.
 *
 * Carries **no** minimum: a minimum is a shippability rule, applied where shippability is decided,
 * in `buildMultiSuppressionSchema` below. A minimum here would reject the first candidate and make
 * the panel unusable.
 */
export const buildMultiFrequencyRulesSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
) =>
  z
    .array(buildMultiFrequencyValueSchema(minAllowedMhz, maxAllowedMhz))
    .max(
      MAX_MULTI_FREQUENCIES,
      t('validation.broadbandSuppression.frequencyListMax', {
        max: MAX_MULTI_FREQUENCIES,
      }),
    )
    .superRefine((frequencies, context) => {
      const hasDuplicate = frequencies.some(
        (frequency, index) => frequencies.indexOf(frequency) !== index,
      );

      if (hasDuplicate) {
        context.addIssue({
          code: 'custom',
          path: [],
          message: t('validation.broadbandSuppression.frequencyListUnique'),
        });
      }

      if (Math.max(...frequencies) - Math.min(...frequencies) > MAX_MULTI_FREQUENCY_SPAN_MHZ) {
        context.addIssue({
          code: 'custom',
          path: [],
          message: t('validation.broadbandSuppression.frequencySpanMax', {
            max: MAX_MULTI_FREQUENCY_SPAN_MHZ,
          }),
        });
      }
    });

export const buildMultiSuppressionSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
  maxSuppressionTime?: number | null,
) =>
  z.object({
    type: z.nativeEnum(SignalType),
    frequencies: buildMultiFrequencyRulesSchema(minAllowedMhz, maxAllowedMhz).min(
      MIN_MULTI_FREQUENCIES,
      t('validation.broadbandSuppression.frequencyListMin'),
    ),
    bandwidth: buildIncrementSchema(
      'validation.broadbandSuppression.bandwidthInvalid',
      'validation.broadbandSuppression.bandwidthRange',
      2,
      50,
    ),
    stepStandingTimeMs: buildIncrementSchema(
      'validation.broadbandSuppression.stepStandingTimeInvalid',
      'validation.broadbandSuppression.stepStandingTimeRange',
      5,
      100,
      5,
      'validation.broadbandSuppression.stepStandingTimeStep',
    ),
    duration: buildDurationSchema(maxSuppressionTime),
  });

export type BroadbandSuppressionFormValues = z.infer<
  ReturnType<typeof buildBroadbandSuppressionSchema>
>;

export type SolidSuppressionFormValues = z.infer<ReturnType<typeof buildSolidSuppressionSchema>>;
export type MultiSuppressionFormValues = z.infer<ReturnType<typeof buildMultiSuppressionSchema>>;
